import React, {
	type ErrorInfo,
	type ReactElement,
	type ReactNode,
	useCallback,
	useEffect,
} from 'react';

import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { EntryPointContainer, type PreloadedEntryPoint } from 'react-relay';

import { useUFOReportError } from '@atlaskit/react-ufo/report-error';
import { reportError } from '@atlaskit/report-error';
import { useEntryPointConfig } from '@atlassian/entry-point-config/use-entry-point-config';
import type { GetRuntimePropsFromEntryPointReference } from '@atlassian/entry-point-types/types';

type OnError = (error: Error, info: ErrorInfo) => void;
export type ErrorFallback = (props: {
	/** The error thrown by the entry point. */
	error: Error;
	/** Closes the entry point's containing surface. */
	onClose: () => void;
}) => ReactElement<any, any> | null;

export type ErrorCallback = (props: {
	/** The error thrown by the entry point. */
	error: Error;
	/** React component-stack information for the error. */
	errorInfo: ErrorInfo;
}) => void;

export type Props<TPreloadedEntryPoint> = {
	entryPointReference: TPreloadedEntryPoint;
	errorFallback?: ErrorFallback;
	fallback: ReactNode;
	forcedError?: boolean;
	id: string;
	onContentReady?: () => void;
	onError?: ErrorCallback;
	placeholderName?: string;
	runtimeProps: GetRuntimePropsFromEntryPointReference<TPreloadedEntryPoint>;
};

function ContentReady({ onContentReady }: { onContentReady?: () => void }): null {
	useEffect(() => {
		onContentReady?.();
	}, [onContentReady]);

	return null;
}

function InternalEntryPointContainerInner<TPreloadedEntryPoint extends PreloadedEntryPoint<any>>({
	entryPointReference,
	errorFallback,
	/** Log and track errors */
	onError: onErrorLocal,
	onContentReady,
	forcedError,
	/** Loading fallback */
	fallback,
	id,
	placeholderName,
	runtimeProps,
}: Props<TPreloadedEntryPoint>) {
	const { PlaceholderComponent } = useEntryPointConfig();

	// useUFOReportError needs to be called under correct UFOSegment to work
	const reportErrorUFO = useUFOReportError();

	const onError = useCallback<OnError>(
		(error, errorInfo) => {
			onErrorLocal?.({ error, errorInfo });

			reportError({
				error,
				errorInfo,
				interactionId: id,
			});

			reportErrorUFO({
				name: id,
				errorMessage: error?.message ?? 'Unknown error',
				errorStack: error?.stack ?? 'Unknown stack',
				forcedError,
			});
		},
		[onErrorLocal, id, reportErrorUFO, forcedError],
	);

	const FallbackComponent = useCallback(
		({ error }: FallbackProps) => {
			const onClose = (runtimeProps as any)?.onClose ?? (() => {});
			return (
				<>
					{errorFallback ? errorFallback({ error, onClose }) : null}
					<ContentReady onContentReady={onContentReady} />
				</>
			);
		},
		[errorFallback, onContentReady, runtimeProps],
	);

	return (
		<ErrorBoundary FallbackComponent={FallbackComponent} onError={onError}>
			<PlaceholderComponent
				name={placeholderName ?? 'internal-internal-entry-point-container'}
				fallback={fallback}
			>
				<EntryPointContainer entryPointReference={entryPointReference} props={runtimeProps} />
				<ContentReady onContentReady={onContentReady} />
			</PlaceholderComponent>
		</ErrorBoundary>
	);
}

export function InternalEntryPointContainer<TPreloadedEntryPoint extends PreloadedEntryPoint<any>>(
	props: Props<TPreloadedEntryPoint>,
): React.JSX.Element {
	const { id } = props;
	const { UFOSegmentComponent } = useEntryPointConfig();

	return (
		<UFOSegmentComponent name={id}>
			<InternalEntryPointContainerInner {...props} />
		</UFOSegmentComponent>
	);
}
